<template>
    <div>
        <div v-if="data.length" class="hidden md:flex flex-col">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="w-full">
                            <thead>
                            <tr>
                                <th v-for="header in headers"
                                    class="px-6 py-4 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider whitespace-no-wrap">

                                    <template v-if="header.showLabel">
                                        <button v-if="header.sortable" type="button"
                                                @click.prevent="changeSort(header)">
                                            <span class="uppercase inline-block mr-4">{{ header.label }}</span>
                                            <font-awesome-icon
                                                v-if="sort.field === header.name"
                                                :key="sort.ascending ? 'sort-up' : 'sort-down'"
                                                :icon="sort.ascending ? 'sort-up' : 'sort-down'"/>
                                            <font-awesome-icon v-else icon="sort"/>
                                        </button>
                                        <span v-else class="uppercase">{{ header.label }}</span>
                                    </template>
                                </th>
                            </tr>
                            </thead>
                            <tbody class="bg-white">
                            <tr v-for="row in rows" class="group odd:bg-green-lighter">
                                <template v-for="column in row">
                                    <component :is="column.component" class="px-6 py-4"/>
                                </template>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="data.length" class="md:hidden space-y-4">
            <dl v-for="row in rows" class="bg-white shadow overflow-hidden rounded-lg">
                <div v-for="(column, i) in row"
                     class="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                     :class="i % 2 === 1 ? 'bg-white' : 'bg-gray-50'">
                    <dt v-if="column.header.showResponsiveLabel"
                        class="text-sm leading-5 font-medium text-gray-500 uppercase">
                        {{ column.header.label }}
                    </dt>
                    <component
                        :is="column.component"
                        as="dd"
                        class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0"
                        :class="column.header.showResponsiveLabel ? 'sm:col-span-2' : 'sm:col-span-3'"/>
                </div>
            </dl>
        </div>
        <div v-if="!data.length" class="border-2 border-dashed border-gray-200 p-4 text-center">
            <p>Nothing to display</p>
        </div>
    </div>
</template>

<script>

import {get} from "lodash";
import {computed, h, provide, ref} from "vue";
import {DataContext} from "./shared";

export default {

    name: "DataComponent",

    props: {
        data: {
            type: Array,
            default: () => ([])
        },
        breakpoint: {
            type: [String, Number],
            default: 768
        },
        initialSort: {
            type: Object,
            default: () => ({field: '', ascending: true})
        },
        disableSort: {
            type: Boolean,
            default: false
        },
    },
    setup(props, {slots, emit}) {
        const
            sort = ref({
                field: props.initialSort.field,
                ascending: props.initialSort.ascending
            }),
            columns = computed(() => {
                return slots.default().filter(item => item.type && item.type.name === 'DataField');
            }),
            headerProps = ref({}),
            headers = computed(() => {
                return columns.value.map(item => {
                    const cProps = ref(item.props);
                    const name = get(cProps.value, 'name');
                    const label = get(cProps.value, 'label', name);
                    const hProps = get(headerProps.value, name, {});

                    return {
                        name,
                        label,
                        sortable: (!props.disableSort && hProps.sortable) || false,
                        showLabel: hProps.showLabel || false,
                        showResponsiveLabel: hProps.showResponsiveLabel || false,

                    };
                });
            }),
            rows = computed(() => {
                return props.data.map(row => {
                    return columns.value.map((item, i) => ({
                        header: get(headers.value, i, {label: ''}),
                        component: h(item, {row})
                    }));
                })
            }),
            changeSort = header => {
                if (!header.name || !header.sortable) {
                    return;
                }

                if (header.name === sort.value.field) {
                    sort.value.ascending = !sort.value.ascending;
                } else {
                    sort.value.field = header.name;
                    sort.value.ascending = true;
                }
                emit('sort', {field: sort.value.field, ascending: sort.value.ascending});
            },
            api = {
                headers,
                headerProps
            };

        provide(DataContext, api);

        return {headers, changeSort, sort, rows, headerProps};
    }
};
</script>
